import type { Slide } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { BaseSceneSwapBridge } from 'js/src/libs/scene-swapping/BaseBridge';
import { updateSceneGroupDurationToEqualVoiceDrivenAssetDuration } from 'js/src/luminary/voicedriven-scenegroup-utils';

/**
 * We want to provide the ability for a user to swap from an ordinary scene to a talking head scene.
 * The use case for this: a user creates an ordinary SceneGroup, then adds a piece of media to the
 * SceneGroup which has a voiceover in it. In this case, we want to allow the user to swap the scene
 * design into a talking head scene design, so that they can use the transcript-related features.
 */
export class OrdinaryToTalkingHeadBridge extends BaseSceneSwapBridge {
    override _from = VideoData.CATEGORY_ORDINARY;
    override _to = VideoData.CATEGORY_TALKING_HEAD;

    // this swap will change something at the SceneGroup level
    override _swapWillAlterSceneGroup = true;

    _getMediaAssetsWithVoiceover(scene: Slide) {
        return scene
            .getAssetsByType(VideoData.MEDIA_ASSET_TYPE, { deep: false })
            .filter((x) => x.isEditable && x.isVideo && x.hasVoiceover);
    }

    override _isSwapAllowedFor(originalScene: Slide): boolean {
        // you can swap a ordinary scene to a talking head scene
        // if it contains a media asset which has_voiceover = true
        return this._getMediaAssetsWithVoiceover(originalScene).length > 0;
    }

    override doPostSwapTransformation(originalScene: Slide, targetScene: Slide): void {
        const mediaAsset = targetScene.sceneGroup.addMediaAsset();
        mediaAsset.sourceData = this._getMediaAssetsWithVoiceover(originalScene)[0].sourceData;
        void updateSceneGroupDurationToEqualVoiceDrivenAssetDuration({
            sceneGroup: targetScene.sceneGroup,
            waitForLuminary: false,
        });
    }
}
